// Notification bell + dropdown for the sidebar. Polls /notifications every
// 30s, shows unread badge count, opens a dropdown with the last 50 items
// when clicked. Clicking an item navigates to that task and marks it read.

import { useState, useEffect, useRef } from 'react';
import * as api from '../lib/api.ts';
import * as push from '../lib/push.ts';
import { navigate } from '../lib/routing.js';
import { fmtDateTime } from '../lib/dates.ts';
import { Icon } from './Icon.jsx';

const POLL_MS = 30_000;

export const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [pushState, setPushState] = useState('unknown');  // 'on' | 'off' | 'denied' | 'unsupported' | 'unknown'
  const [pushBusy, setPushBusy] = useState(false);
  const ref = useRef(null);

  // Detect current push subscription state when dropdown opens.
  useEffect(() => {
    if (!open) return;
    (async () => {
      if (!push.pushSupported()) { setPushState('unsupported'); return; }
      const perm = push.pushPermission();
      if (perm === 'denied') { setPushState('denied'); return; }
      const sub = await push.getExistingSubscription().catch(() => null);
      setPushState(sub ? 'on' : 'off');
    })();
  }, [open]);

  async function togglePush() {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (pushState === 'on') {
        await push.unsubscribeFromPush();
        setPushState('off');
      } else {
        await push.subscribeToPush();
        setPushState('on');
      }
    } catch (e) {
      alert(e.message || '推送设置失败');
    } finally {
      setPushBusy(false);
    }
  }

  // Initial fetch + polling. Cancels on unmount.
  useEffect(() => {
    let cancelled = false;
    let timer;
    async function tick() {
      try {
        const data = await api.loadNotifications();
        if (cancelled) return;
        setItems(data.notifications);
        setUnread(data.unread);
        // Title bar badge: layer "(N) " on top of whatever the route set
        // (App.jsx's per-route document.title effect). Strip any prior (N)
        // prefix first so polling doesn't stack them.
        const base = document.title.replace(/^\(\d+\)\s+/, '');
        document.title = data.unread > 0 ? `(${data.unread}) ${base}` : base;
      } catch (e) {
        // 401 = session expired; let App's loader handle it. Otherwise stay silent.
        if (e?.status && e.status !== 401) console.warn('notifications fetch failed:', e.message);
      } finally {
        if (!cancelled) timer = setTimeout(tick, POLL_MS);
      }
    }
    tick();
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    function onDoc(e) { if (!ref.current?.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  async function openNotification(n) {
    setOpen(false);
    if (n.readAt === null) {
      try {
        await api.markNotificationRead(n.id);
        setItems(prev => prev.map(x => x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x));
        setUnread(u => Math.max(0, u - 1));
      } catch { /* best effort */ }
    }
    if (n.taskId) navigate('/task/' + n.taskId);
  }

  async function markAll() {
    try {
      await api.markAllNotificationsRead();
      const now = new Date().toISOString();
      setItems(prev => prev.map(x => ({ ...x, readAt: x.readAt || now })));
      setUnread(0);
    } catch { /* best effort */ }
  }

  return (
    <div className="notif-wrap" ref={ref}>
      <button className="notif-bell" onClick={() => setOpen(o => !o)} aria-label={`通知 (${unread} 未读)`}>
        <Icon name="bell" className="icon"/>
        {unread > 0 && <span className="notif-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div className="notif-dropdown" role="menu">
          <div className="notif-header">
            <span>通知</span>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              {pushState !== 'unsupported' && pushState !== 'unknown' && (
                <button className="notif-mark-all"
                        onClick={togglePush}
                        disabled={pushBusy || pushState === 'denied'}
                        title={pushState === 'denied' ? '浏览器已禁用通知权限' : '浏览器推送'}>
                  {pushBusy
                    ? '...'
                    : pushState === 'on' ? '🔔 推送已开' :
                      pushState === 'denied' ? '🚫 推送被禁' : '🔕 开启推送'}
                </button>
              )}
              {unread > 0 && (
                <button className="notif-mark-all" onClick={markAll}>全部标为已读</button>
              )}
            </div>
          </div>
          {items.length === 0 ? (
            <div className="notif-empty">还没有通知</div>
          ) : (
            <div className="notif-list">
              {items.map(n => (
                <button key={n.id} className={`notif-item ${n.readAt ? '' : 'notif-unread'}`}
                        onClick={() => openNotification(n)}>
                  <div className="notif-msg">{n.message}</div>
                  <div className="notif-time">{fmtDateTime(n.createdAt)}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
